import type { GameEvent } from '../schema';

export const BUSINESS_EVENTS: GameEvent[] = [
  {
    id: 'business.theIdea',
    title: 'You cannot stop thinking about it',
    description:
      'There is a thing you could build. Doing it properly means capital, time, and the end of a predictable income.',
    category: 'business',
    pools: ['pool.business'],
    weight: 7,
    cooldown: 6,
    requires: [
      { k: 'flag', flag: 'founder', value: false },
      { k: 'age', min: 22, max: 62 },
    ],
    weightMods: [
      { conditions: [{ k: 'trait', trait: 'ambition', op: '>=', value: 62 }], multiplier: 1.6 },
      { conditions: [{ k: 'aspiration', defId: 'dream.business', status: 'active' }], multiplier: 2.4 },
      { conditions: [{ k: 'num', path: 'meaningAlignment', op: '<', value: 45 }], multiplier: 1.4 },
    ],
    choices: [
      {
        id: 'sideProject',
        label: 'Build it on the side',
        detail: 'Keep the salary. Lose the evenings.',
        hint: 'mixed',
        tags: ['entrepreneurship', 'pragmatic'],
        outcomes: [
          {
            weight: 55,
            label: 'It found customers',
            narrative: 'Nights and weekends for a year, and then somebody paid you without being asked twice.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'trait', trait: 'discipline', op: '>=', value: 60 }], multiplier: 1.8 },
              { conditions: [{ k: 'num', path: 'lifeLoad', op: '<=', value: 55 }], multiplier: 1.5 },
            ],
            effects: [
              { k: 'business', op: 'start', archetypeId: 'biz.freelance', capital: 3000 },
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'stat', stat: 'purpose', delta: 12 },
              { k: 'stat', stat: 'energy', delta: -10 },
              { k: 'burnout', op: 'load', delta: 10 },
              { k: 'memory', title: 'Started something of your own', tone: 'milestone', category: 'business', weight: 8 },
            ],
          },
          {
            weight: 45,
            label: 'It never launched',
            narrative: 'A domain name, a half-built prototype, and eleven months of intending to finish it.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -0.8, note: 'side project costs' },
              { k: 'stat', stat: 'energy', delta: -8 },
              { k: 'inertia', op: 'pressure', delta: 8 },
              { k: 'intention', op: 'add', id: 'int.launch', label: 'Actually launch the business', topic: 'business', urgency: 30, resumeEventId: 'business.finallyLaunch', decayEventId: 'business.neverLaunched' },
            ],
          },
        ],
      },
      {
        id: 'allIn',
        label: 'Quit and go all in',
        detail: 'No salary. Full attention. Real risk.',
        hint: 'risky',
        requires: [{ k: 'num', path: 'runwayMonths', op: '>=', value: 6 }],
        showWhenLocked: true,
        tags: ['entrepreneurship', 'risk', 'courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Went full-time',
            narrative: 'You handed in notice and told everyone it was the sensible time to do it.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'quit' },
              { k: 'business', op: 'start', archetypeId: 'biz.services', capital: 18000 },
              { k: 'stat', stat: 'purpose', delta: 18 },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'trait', trait: 'riskTolerance', delta: 6 },
              { k: 'memory', title: 'Quit to build a business', tone: 'milestone', category: 'business', weight: 9 },
            ],
          },
        ],
      },
      {
        id: 'later',
        label: 'Not the right time',
        hint: 'safe',
        defers: { id: 'int.business', label: 'Start the business', topic: 'business', urgency: 26, resumeEventId: 'business.finallyLaunch', decayEventId: 'business.neverLaunched' },
        outcomes: [
          {
            weight: 1,
            label: 'Shelved it',
            narrative: 'When the mortgage is smaller. When the children are older. When.',
            tone: 'neutral',
            effects: [{ k: 'inertia', op: 'pressure', delta: 5 }],
          },
        ],
      },
    ],
  },
  {
    id: 'business.finallyLaunch',
    title: 'The idea is still there',
    description: 'Years later, the thing you meant to build has not gone away. Neither have the reasons not to.',
    category: 'business',
    pools: ['pool.business', 'pool.inertia'],
    weight: 6,
    requires: [{ k: 'intention', topic: 'business', present: true }, { k: 'flag', flag: 'founder', value: false }],
    choices: [
      {
        id: 'go',
        label: 'Do it now',
        hint: 'risky',
        tags: ['entrepreneurship', 'courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Finally started',
            narrative: 'Late, more cautious, and considerably better funded than the twenty-six-year-old version would have been.',
            tone: 'positive',
            effects: [
              { k: 'intention', op: 'resolve', id: 'int.business' },
              { k: 'intention', op: 'resolve', id: 'int.launch' },
              { k: 'business', op: 'start', archetypeId: 'biz.consulting', capital: 14000 },
              { k: 'stat', stat: 'purpose', delta: 16 },
              { k: 'habit', habit: 'procrastination', delta: -8 },
              { k: 'aspiration', op: 'progress', defId: 'dream.business', delta: 100 },
            ],
          },
        ],
      },
      {
        id: 'no',
        label: 'Let it go',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Deferred again',
            narrative: 'It went back in the drawer with the others.',
            tone: 'negative',
            effects: [
              { k: 'intention', op: 'defer', id: 'int.business' },
              { k: 'inertia', op: 'pressure', delta: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'business.neverLaunched',
    title: 'Someone else built it',
    description: 'The idea you sat on for a decade now has a logo, a funding round, and a founder who is not you.',
    category: 'business',
    pools: ['pool.inertia'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'ack',
        label: 'Read the article',
        outcomes: [
          {
            weight: 1,
            label: 'Watched it happen without you',
            narrative: 'You were right about the idea. That turns out to be worth almost nothing.',
            tone: 'negative',
            effects: [
              { k: 'regret', op: 'add', id: 'regret.business', label: 'Never built the thing you thought of first', weight: 26 },
              { k: 'stat', stat: 'purpose', delta: -8 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'memory', title: 'Someone else built your idea', tone: 'negative', category: 'business', weight: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'business.growthCrossroads',
    title: 'Grow, or stay small',
    description:
      'Demand exceeds what you can personally deliver. Scaling means hiring, debt, and becoming a manager.',
    category: 'business',
    pools: ['pool.business'],
    weight: 8,
    cooldown: 5,
    requires: [{ k: 'num', path: 'businessHealth', op: '>=', value: 48 }, { k: 'num', path: 'businessRevenue', op: '>=', value: 28000 }],
    choices: [
      {
        id: 'scale',
        label: 'Hire and scale',
        hint: 'risky',
        tags: ['entrepreneurship', 'risk', 'ambition'],
        outcomes: [
          {
            weight: 58,
            label: 'It scaled',
            narrative: 'Four people, a real office, and revenue that no longer depends on you personally showing up.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'skill', skill: 'management', op: '>=', value: 55 }], multiplier: 1.9 },
              { conditions: [{ k: 'num', path: 'cash', op: '>=', value: 30000 }], multiplier: 1.4 },
            ],
            effects: [
              { k: 'business', op: 'adjust', field: 'growth', delta: 0.14 },
              { k: 'business', op: 'adjust', field: 'timeDemand', delta: 8 },
              { k: 'business', op: 'adjust', field: 'risk', delta: 10 },
              { k: 'skill', skill: 'management', delta: 8 },
              { k: 'stat', stat: 'stress', delta: 10 },
            ],
          },
          {
            weight: 42,
            label: 'Overextended',
            narrative: 'Payroll arrived monthly whether the customers did or not.',
            tone: 'negative',
            effects: [
              { k: 'business', op: 'adjust', field: 'health', delta: -24 },
              { k: 'business', op: 'adjust', field: 'risk', delta: 22 },
              { k: 'debt', op: 'add', kind: 'business', label: 'Working capital loan', amount: 40000, rate: 0.098, years: 6 },
              { k: 'stat', stat: 'stress', delta: 20 },
              { k: 'burnout', op: 'load', delta: 14 },
            ],
          },
        ],
      },
      {
        id: 'staySmall',
        label: 'Stay small and profitable',
        hint: 'safe',
        tags: ['balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Kept it small',
            narrative: 'You capped the business at a size you could run without hating it. Underrated move.',
            tone: 'positive',
            effects: [
              { k: 'business', op: 'adjust', field: 'health', delta: 12 },
              { k: 'business', op: 'adjust', field: 'risk', delta: -14 },
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'stat', stat: 'stress', delta: -6 },
            ],
          },
        ],
      },
      {
        id: 'raise',
        label: 'Raise outside money',
        hint: 'risky',
        requires: [{ k: 'num', path: 'businessValuation', op: '>=', value: 70000 }],
        showWhenLocked: true,
        tags: ['risk', 'ambition'],
        outcomes: [
          {
            weight: 40,
            label: 'Funded',
            narrative: 'Someone believed the numbers. You now have a board and a deadline.',
            tone: 'positive',
            effects: [
              { k: 'business', op: 'invest', amount: 0 },
              { k: 'business', op: 'adjust', field: 'growth', delta: 0.24 },
              { k: 'business', op: 'adjust', field: 'timeDemand', delta: 12 },
              { k: 'business', op: 'adjust', field: 'risk', delta: 18 },
              { k: 'stat', stat: 'stress', delta: 14 },
            ],
          },
          {
            weight: 60,
            label: 'No thanks',
            narrative: 'Nine meetings, nine polite declines, and four months you will never account for.',
            tone: 'negative',
            effects: [
              { k: 'business', op: 'adjust', field: 'health', delta: -8 },
              { k: 'stat', stat: 'happiness', delta: -7 },
              { k: 'stat', stat: 'energy', delta: -8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'business.acquisitionOffer',
    title: 'Someone wants to buy it',
    description: 'A real offer, in writing. Taking it ends the thing you built; refusing it may be the last chance.',
    category: 'business',
    pools: ['pool.business', 'pool.opportunity'],
    weight: 4,
    cooldown: 8,
    requires: [{ k: 'num', path: 'businessValuation', op: '>=', value: 42000 }, { k: 'num', path: 'businessHealth', op: '>=', value: 42 }],
    choices: [
      {
        id: 'sell',
        label: 'Sell',
        hint: 'mixed',
        tags: ['finance', 'exit'],
        outcomes: [
          {
            weight: 1,
            label: 'Sold the business',
            narrative: 'Money in the account and a strange, hollow Monday morning.',
            tone: 'positive',
            effects: [
              { k: 'business', op: 'exit', multiple: 1 },
              { k: 'stat', stat: 'happiness', delta: 10 },
              { k: 'stat', stat: 'purpose', delta: -12 },
              { k: 'achievement', id: 'ach.exit', label: 'Sold a business', category: 'business' },
              { k: 'memory', title: 'Sold the business', tone: 'milestone', category: 'business', weight: 9 },
              { k: 'triggerEvent', eventId: 'business.afterExit', afterYears: 1 },
            ],
          },
        ],
      },
      {
        id: 'negotiate',
        label: 'Push for more',
        hint: 'risky',
        requires: [{ k: 'skill', skill: 'business', op: '>=', value: 55 }],
        showWhenLocked: true,
        tags: ['risk', 'ambition'],
        outcomes: [
          {
            weight: 45,
            label: 'They improved it',
            narrative: 'Forty per cent more, for the price of two tense weeks.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 60 }], multiplier: 1.7 }],
            effects: [
              { k: 'business', op: 'exit', multiple: 1.4 },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'achievement', id: 'ach.exit', label: 'Sold a business well', category: 'business' },
            ],
          },
          {
            weight: 55,
            label: 'They walked',
            narrative: 'The offer was withdrawn and never came back.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'business', op: 'adjust', field: 'health', delta: -6 },
              { k: 'regret', op: 'add', id: 'regret.exit', label: 'Turned down the offer', weight: 14 },
            ],
          },
        ],
      },
      {
        id: 'keep',
        label: 'Keep building',
        hint: 'mixed',
        tags: ['entrepreneurship'],
        outcomes: [
          {
            weight: 1,
            label: 'Declined',
            narrative: 'It is not finished. That is either conviction or an inability to stop.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'business', op: 'adjust', field: 'growth', delta: 0.04 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'business.afterExit',
    title: 'Now what?',
    description: 'You have money and no structure. It turns out the structure was doing a lot of work.',
    category: 'business',
    pools: ['pool.business', 'pool.legacy'],
    weight: 0,
    choices: [
      {
        id: 'again',
        label: 'Start something else',
        hint: 'mixed',
        tags: ['entrepreneurship'],
        outcomes: [
          {
            weight: 1,
            label: 'Back in',
            narrative: 'You lasted eleven months as a retired person.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 14 },
              { k: 'triggerEvent', eventId: 'business.theIdea', afterYears: 1 },
              { k: 'flag', flag: 'founder', value: false },
            ],
          },
        ],
      },
      {
        id: 'invest',
        label: 'Put the money to work and stop',
        hint: 'safe',
        tags: ['finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Invested and stepped back',
            narrative: 'A balanced portfolio and a calendar with space in it.',
            tone: 'positive',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'balanced', amount: 120000 },
              { k: 'stat', stat: 'stress', delta: -12 },
              { k: 'stat', stat: 'energy', delta: 10 },
            ],
          },
        ],
      },
      {
        id: 'give',
        label: 'Fund other people',
        hint: 'safe',
        tags: ['contribution', 'legacy'],
        outcomes: [
          {
            weight: 1,
            label: 'Became the person who writes the first cheque',
            narrative: 'You backed three people who reminded you of yourself. Two of them will fail.',
            tone: 'positive',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'speculative', amount: 60000 },
              { k: 'counter', name: 'contributions', delta: 3 },
              { k: 'aspiration', op: 'progress', defId: 'dream.contribute', delta: 40 },
              { k: 'stat', stat: 'purpose', delta: 16 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'business.cashCrunch',
    title: 'Payroll is on Friday',
    description: 'A large customer has not paid. The obligations have not noticed.',
    category: 'business',
    pools: ['pool.business', 'pool.crisis'],
    weight: 7,
    cooldown: 4,
    requires: [{ k: 'num', path: 'businessRevenue', op: '>', value: 0 }],
    weightMods: [
      { conditions: [{ k: 'num', path: 'businessHealth', op: '<', value: 45 }], multiplier: 2.1 },
      { conditions: [{ k: 'str', path: 'marketRegime', op: '==', value: 'bust' }], multiplier: 2.4 },
    ],
    choices: [
      {
        id: 'personal',
        label: 'Cover it personally',
        hint: 'costly',
        tags: ['entrepreneurship', 'risk'],
        outcomes: [
          {
            weight: 1,
            label: 'Bridged it yourself',
            narrative: 'Your savings became the company’s savings. The line between them was always notional.',
            tone: 'neutral',
            effects: [
              { k: 'business', op: 'invest', amount: 22000 },
              { k: 'stat', stat: 'stress', delta: 16 },
              { k: 'relationshipAll', kind: 'spouse', dim: 'conflict', delta: 12 },
            ],
          },
        ],
      },
      {
        id: 'borrow',
        label: 'Borrow against the business',
        hint: 'mixed',
        outcomes: [
          {
            weight: 1,
            label: 'Took the facility',
            narrative: 'Expensive money, quickly. Better than the alternative on Friday.',
            tone: 'neutral',
            effects: [
              { k: 'debt', op: 'add', kind: 'business', label: 'Emergency facility', amount: 28000, rate: 0.135, years: 4 },
              { k: 'business', op: 'adjust', field: 'health', delta: -8 },
              { k: 'stat', stat: 'stress', delta: 12 },
            ],
          },
        ],
      },
      {
        id: 'cut',
        label: 'Cut deep, immediately',
        hint: 'costly',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Made the cuts',
            narrative: 'Two redundancies and a conversation you will remember for a decade.',
            tone: 'negative',
            effects: [
              { k: 'business', op: 'adjust', field: 'health', delta: 10 },
              { k: 'business', op: 'adjust', field: 'growth', delta: -0.1 },
              { k: 'business', op: 'adjust', field: 'timeDemand', delta: 10 },
              { k: 'stat', stat: 'happiness', delta: -10 },
              { k: 'skill', skill: 'management', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
];
